import { toValue } from 'vue'
import { debounce, isEmpty, mapValues, negate, pickBy, startCase } from 'lodash-es'
import numbro from 'numbro'
import { formatISO, intlFormatDistance, parseISO } from 'date-fns'
import { createRound } from 'commons-mathrail/shared/fns'
import { currentLocale } from '../i18n'

export { v4 as randomUUID } from 'uuid'

// Generic
export const extractErrorParams = error => JSON.parse(JSON.stringify(error, Object.getOwnPropertyNames(error)))

// Predicates
export const isFieldEmpty = value => value == null || (typeof value === 'string' && String(value).trim() === '')

// Rounding
export const roundDistance = createRound(100)
export const roundPowerKW = createRound(1)
export const roundVoltage = voltage => Math.round(voltage || 0)
export const roundCurrent = current => Math.round(current || 0)

// Formatting
export const cut = (length, text) =>
  length && text?.length > length
    ? String(text).substring(0, length) + '…'
    : text
// export const formatLocaleDate = date => new Date(date).toLocaleDateString(currentLocale.value)
const dateTimeFormat = () =>
  Intl.DateTimeFormat(currentLocale.value, { dateStyle: 'medium', timeStyle: 'short' })
export const formatDateTime = date => date && dateTimeFormat().format(new Date(date))
export const formatISODate = value => formatISO(value, { representation: 'date' })
export const formatDuration = seconds =>
  new Date(1_000 * (seconds || 0))
    .toISOString()
    .substring(11, 11 + 8)
export const formatTimeDistance = date =>
  intlFormatDistance(parseISO(date), new Date(), { locale: currentLocale.value })
export const formatPercent = (value, format = {
  output: 'percent',
  spaceSeparated: numbro.languageData().spaceSeparated ?? false, // Boolean required by numbro type checks #MAT-540
  mantissa: 0
}) =>
  numbro(value).format(format)
const formatNumber = format => value => value == null ? '' : numbro(value).format(format)
export const formatInteger = formatNumber({ thousandSeparated: true, mantissa: 0 })
export const formatDecimal = mantissa => formatNumber({ thousandSeparated: true, mantissa })

// HTML
export const blurFocusedElement = (selector, { optional } = {}) => {
  const element = window.document.querySelector(`${selector} *:focus`)
  if (element) {
    element?.blur()
  } else if (!optional) {
    console.warn(`No focused element found for selector: ${selector}`)
  }
}
export const altKey = key => event => {
  const match = event.altKey && event.code === key
  if (match) {
    event.preventDefault()
  }
  return match
}

// Forms
export const createMatches = searchRef => value =>
  !searchRef.value || value?.toLowerCase().includes(searchRef.value.toLowerCase())

export const stringArrayToBooleans = array =>
  Object.fromEntries(
    (array ?? []).map(key => [key, true])
  )

export const debounceDelayOnTyping = 400
export const debounceOnTyping = fn => debounce(fn, debounceDelayOnTyping)

export const toQuery = formData => pickBy(mapValues(formData, toValue), negate(isFieldEmpty))

export const toOptions = values => values.map(value => ({ value, label: startCase(value) }))

// Objects
export const isRowEmpty = row => Object.values(row).every(isEmpty)

// Data fetching
export const fetcher = url => () => fetch(url).then(response => response.json())
